import { supabase } from '../lib/supabase'

export interface Announcement {
  id: string
  title: string
  content: string
  image_path?: string
  url?: string
  published_at: string
  created_at: string
  updated_at: string
}

export interface AnnouncementInsert {
  title: string
  content: string
  image_path?: string
  url?: string
  published_at: string
}

export interface AnnouncementUpdate {
  title?: string
  content?: string
  image_path?: string
  url?: string
  published_at?: string
}

export class AnnouncementService {
  // Get published announcements for users (published_at <= now)
  static async getPublishedAnnouncements(): Promise<Announcement[]> {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  // Get all announcements for admin (including future-dated)
  static async getAllAnnouncements(): Promise<Announcement[]> {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  // Get announcement by ID
  static async getAnnouncementById(id: string): Promise<Announcement | null> {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw error
    }
    return data
  }

  // Create new announcement
  static async createAnnouncement(announcement: AnnouncementInsert): Promise<Announcement> {
    const { data, error } = await supabase
      .from('announcements')
      .insert(announcement)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update announcement
  static async updateAnnouncement(id: string, updates: AnnouncementUpdate): Promise<Announcement> {
    const { data, error } = await supabase
      .from('announcements')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Delete announcement
  static async deleteAnnouncement(id: string): Promise<void> {
    const { error } = await supabase
      .from('announcements')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Count published announcements
  static async getPublishedAnnouncementCount(): Promise<number> {
    const { count, error } = await supabase
      .from('announcements')
      .select('*', { count: 'exact', head: true })
      .lte('published_at', new Date().toISOString())

    if (error) throw error
    return count || 0
  }

  // Get latest 2 published announcements for banner display
  static async getLatestAnnouncements(limit: number = 2): Promise<Announcement[]> {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // Upload image to Supabase storage
  static async uploadImage(file: File): Promise<string> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
    const filePath = `images/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('announcements')
      .upload(filePath, file)

    if (uploadError) throw uploadError
    return filePath
  }

  // Delete image from Supabase storage
  static async deleteImage(imagePath: string): Promise<void> {
    const { error } = await supabase.storage
      .from('announcements')
      .remove([imagePath])

    if (error) throw error
  }

  // Get public URL for image
  static getImageUrl(imagePath: string): string {
    const { data } = supabase.storage
      .from('announcements')
      .getPublicUrl(imagePath)

    return data.publicUrl
  }
}